/**
 * Waiting-room presence policy (testmap #12 — HDFC customer "could not even join", 20-Aug live).
 *
 * RCA half: spurious iOS `queue:leave` beacons during ACTIVE presence poisoned the agent's view of
 * the customer. iOS Safari flaps `visibilitychange:hidden` (notification shade, permission sheets,
 * an app-switcher peek) and fires `pagehide` with `persisted=true` on mere backgrounding — the old
 * handler beaconed `queue:leave` on every one of them, so a customer who was demonstrably present
 * (heartbeating) read as gone, and the be's claim guard (409 CUSTOMER_NOT_PRESENT) or the officer's
 * own judgement acted on the lie.
 *
 * Policy, extracted pure so it unit-tests cleanly:
 *   • hidden → DEBOUNCE (default 15s, well inside the be's 45s presence-staleness window): only a
 *     page that STAYS hidden sends a leave. iOS freezes timers while hidden, so the debounced send
 *     may never fire — that is the honest degradation: the be's staleness window covers a customer
 *     who is genuinely gone, and no beacon at all beats a false one.
 *   • visible again → cancel; the poll's immediate re-tick (queue.ts) re-stamps presence.
 *   • pagehide persisted=true → NOT a teardown (bfcache/backgrounding) — same debounce class.
 *   • pagehide persisted=false → real teardown → send immediately.
 *   • NEVER a leave while the officer is on the call (the claim/handoff window): a leave beacon
 *     there is precisely the poison the RCA traced — the customer is transitioning to the call
 *     screen, not abandoning the queue.
 */

/** Debounce for hidden-state leaves — well inside the be's PT45S presence staleness. */
export const LEAVE_DEBOUNCE_MS = 15_000;

export interface LeaveBeaconGuard {
  /** visibilitychange → hidden. */
  onHidden(): void;
  /** visibilitychange → visible. */
  onVisible(): void;
  /** pagehide — `persisted` is the bfcache flag (true = the page may come back). */
  onPageHide(persisted: boolean): void;
  /** Unmount: cancel any pending send (never beacon after leaving the screen for the call). */
  dispose(): void;
}

export function createLeaveBeaconGuard(opts: {
  /** Fire the actual keepalive beacon (api/queue.leaveQueue, bound to the session). */
  send: () => void;
  /** Live read: is an officer bound to the interaction right now (the handoff window)? */
  officerOnCall: () => boolean;
  debounceMs?: number;
}): LeaveBeaconGuard {
  const debounceMs = opts.debounceMs ?? LEAVE_DEBOUNCE_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const cancel = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const fire = (): void => {
    // Checked at SEND time, not schedule time — a claim landing mid-debounce must still suppress.
    if (disposed || opts.officerOnCall()) return;
    opts.send();
  };

  return {
    onHidden(): void {
      if (disposed || opts.officerOnCall()) return;
      cancel();
      timer = setTimeout(() => {
        timer = null;
        fire();
      }, debounceMs);
    },
    onVisible(): void {
      cancel();
    },
    onPageHide(persisted: boolean): void {
      if (disposed) return;
      if (persisted) {
        // Backgrounded, not torn down — the spurious class. Debounce like hidden.
        this.onHidden();
        return;
      }
      cancel();
      fire();
    },
    dispose(): void {
      disposed = true;
      cancel();
    },
  };
}

/**
 * The advance-to-call latch. The waiting room advances on the claim/handoff state the heartbeat
 * wire serves (`source='ready' && agentPresent`), but the 900ms confirmation beat rides a
 * setTimeout that iOS freezes while backgrounded — and a visibility flush firing NEXT TO the
 * thawed timer must not advance twice (waiting→call→complete would skip the call). One latch,
 * every path through it.
 */
export function createAdvanceOnce(advance: () => void): () => void {
  let fired = false;
  return () => {
    if (fired) return;
    fired = true;
    advance();
  };
}
